import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { CloseDoorResponse, DispatchBoardQueueLoad } from "app/data/city-delivery/dispatch-board/models";
import { useCloseDoor } from "features/city-delivery/dispatch-board/hooks/useDoor";
import { userSelector } from "slices/user/userSlice";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import { toast } from "react-toastify";

export interface CloseDoorProps {
  show: boolean;
  onClose: () => void;
  dispatchLoad: DispatchBoardQueueLoad;
  onCloseDoorSuccess?: (manifestNumber: number) => void;
}

export const CloseDoor: React.FC<CloseDoorProps> = (props) => {
  const { show, onClose, dispatchLoad, onCloseDoorSuccess } = props;
  const terminalNumber = useSelector(userSelector)?.activeTerminal?.id;
  const doorNumber = dispatchLoad?.door ?? "";
  const trailerNumber = dispatchLoad?.trailerNumber ?? "";
  const picklistNumber = dispatchLoad?.picklistNumber;
  const carrierNumber = dispatchLoad?.carrierNumber;

  const handleSuccess = useCallback((responseData?: CloseDoorResponse) => {
    const manifestNumber =
      responseData?.delivery_manifest_number != null
        ? Number(responseData.delivery_manifest_number)
        : NaN;
    const manifestText =
      !Number.isNaN(manifestNumber) ? ` Manifest number: ${manifestNumber}.` : "";
    toast.info(`Door closed successfully.${manifestText}`);
    onClose();
    if (!Number.isNaN(manifestNumber)) {
      onCloseDoorSuccess?.(manifestNumber);
    }
  }, [onClose, onCloseDoorSuccess]);

  const handleError = useCallback((e: any) => {
    const msg =
      e?.response?.data?.detail ?? e?.data?.detail ?? "Failed to close door";
    toast.error(msg);
  }, []);

  const closeDoorMutation = useCloseDoor(handleSuccess, handleError);

  const handleConfirm = useCallback(() => {
    if (!terminalNumber || !picklistNumber || !doorNumber) return;
    closeDoorMutation.mutate({
      terminalNumber,
      doorNumber,
      picklistNumber,
      trailerNumber: trailerNumber || undefined,
      carrierNumber,
    });
  }, [
    terminalNumber,
    picklistNumber,
    carrierNumber,
    doorNumber,
    trailerNumber,
    closeDoorMutation,
  ]);

  const message = (
    <p className="close-door__message">
      Are you sure you want to close <strong>Door {doorNumber}</strong> with{" "}
      <strong>trailer {trailerNumber || "—"}</strong>?
      <br />
      <br />
      Once a door is closed, no more shipments can be loaded into it. If you expect more items to be
      loaded for this trailer, please keep the door in loading status and close it only when all
      items are loaded.
    </p>
  );

  return (
    <ConfirmationModal
      opened={show}
      header="Close door"
      onCancel={onClose}
      onConfirm={handleConfirm}
      cancelButtonText="Cancel"
      confirmButtonText="Close door"
      spinner={closeDoorMutation.isLoading}
      disableConfirmButton={closeDoorMutation.isLoading}
      fitContent
    >
      {message}
    </ConfirmationModal>
  );
};
